import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { useNavigate } from 'react-router-dom';

import {
  AddOnPurchase,
  CreditLine,
  PlanPurchase,
  Subscription,
} from '@/services/hsm-backend';

export interface AccountSummaryProps {
  subscription?: Subscription;
  companyId: string;
}

/**
 * "Resumen de cuenta" section: heading + subtitle + a 3-column grid of
 * KPI cards (Plan, Paquete, Línea de crédito). Each KPI renders the
 * **assigned capacity** for that item (messages contracted / available),
 * not the runtime consumption — `new-hsm-admin` is a management tool
 * for sales, not the customer's usage dashboard. Falls back to "—" when
 * the company has no subscription / no items of that kind.
 *
 * @param subscription Active subscription; optional because companies may
 *                     not have one (free tier / inactive).
 */
export const AccountSummary = ({
  subscription,
  companyId,
}: AccountSummaryProps) => {
  const navigate = useNavigate();
  const goTo = (tab: 'plan' | 'add-ons' | 'credit-line') =>
    navigate(`/company/${companyId}/${tab}`);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" component="h2">
          Resumen de cuenta
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Estado actual de suscripción, plan y líneas HSM de esta empresa.
        </Typography>
      </Box>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={2}
        alignItems="stretch"
      >
        <Box flex={1} minWidth={0}>
          <PlanKpi
            plan={subscription?.activePlan}
            onClick={() => goTo('plan')}
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <AddOnsKpi
            addOns={subscription?.addOnPurchases ?? []}
            onClick={() => goTo('add-ons')}
          />
        </Box>
        <Box flex={1} minWidth={0}>
          <CreditLineKpi
            creditLine={subscription?.creditLine}
            onClick={() => goTo('credit-line')}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

const PlanKpi = ({
  plan,
  onClick,
}: {
  plan?: PlanPurchase;
  onClick: () => void;
}) => (
  <KpiCard
    icon={<BoltIcon fontSize="small" />}
    title="Plan"
    value={plan ? plan.messageQty.toLocaleString('es-AR') : '—'}
    caption={plan ? 'mensajes/mes' : undefined}
    foot={plan ? `Renovación: ${formatShortDate(plan.updatedAt)}` : '—'}
    onClick={onClick}
  />
);

const AddOnsKpi = ({
  addOns,
  onClick,
}: {
  addOns: AddOnPurchase[];
  onClick: () => void;
}) => {
  const active = addOns.filter((a) => a.status === 'active');
  const totalQty = active.reduce((acc, a) => acc + a.messageQty, 0);
  const label = active.length
    ? `${active.length} paquete${active.length === 1 ? '' : 's'} activo${active.length === 1 ? '' : 's'}`
    : 'Sin paquetes activos';

  return (
    <KpiCard
      icon={<Inventory2OutlinedIcon fontSize="small" />}
      title="Paquete"
      value={active.length ? totalQty.toLocaleString('es-AR') : '—'}
      caption={active.length ? 'mensajes' : undefined}
      foot={label}
      onClick={onClick}
    />
  );
};

const CreditLineKpi = ({
  creditLine,
  onClick,
}: {
  creditLine?: CreditLine;
  onClick: () => void;
}) => (
  <KpiCard
    icon={<CreditCardIcon fontSize="small" />}
    title="Línea de crédito"
    value={creditLine ? creditLine.messageQty.toLocaleString('es-AR') : '—'}
    caption={creditLine ? 'mensajes' : undefined}
    onClick={onClick}
    foot={
      creditLine ? (
        <Chip
          size="small"
          label={creditLine.status === 'active' ? 'Activa' : 'Inactiva'}
          color={creditLine.status === 'active' ? 'success' : 'default'}
        />
      ) : (
        '—'
      )
    }
  />
);

interface KpiCardProps {
  icon: JSX.Element;
  title: string;
  value: string;
  caption?: string;
  foot: React.ReactNode;
  /** Navigates to the matching billing tab when the card is clicked. */
  onClick: () => void;
}

const KpiCard = ({
  icon,
  title,
  value,
  caption,
  foot,
  onClick,
}: KpiCardProps) => (
  <Card sx={{ height: '100%' }}>
    <CardActionArea
      onClick={onClick}
      aria-label={`Ir a ${title}`}
      sx={{ height: '100%' }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          color="text.secondary"
        >
          {icon}
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          alignItems="baseline"
          spacing={0.75}
          sx={{ mt: 1.5 }}
        >
          <Typography variant="h3" component="div" fontWeight={700}>
            {value}
          </Typography>
          {caption && (
            <Typography variant="body2" color="text.secondary">
              {caption}
            </Typography>
          )}
        </Stack>
        <Box sx={{ mt: 1.5 }}>
          {typeof foot === 'string' ? (
            <Typography variant="caption" color="text.secondary">
              {foot}
            </Typography>
          ) : (
            foot
          )}
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

function formatShortDate(value: string): string {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString('es-AR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}
